import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { CloudFunctionsService } from 'services/cloud-functions.service';
import { AuthService } from 'services/auth.service';
import { CouchLoginReason } from 'services/couch-login-storm';
import { LoggingService } from 'services/logging.service';
import { PayProPriceFetcher, PlanOption } from 'services/paypro-price-fetcher.service';
import { IAPService } from 'services/iap.service';
import {
  SubscriptionStrategy, SwitchPlanResult,
  SubscriptionProvider, createSubscriptionStrategy,
  CancelSubscriptionParams, CancelSubscriptionResult, PaddleSubscriptionStrategy,
} from 'services/subscription-strategy';
import { PaddleSwitchTarget } from '@cf-shared/google-play-interface';
import { SubscriptionStatus, isPaddleSubscriberInFreeTrial, isPausedPaddleSubscriber } from '@cf-shared/data-model/couch-user';
import { isWebBilledPaymentFailing } from '@cf-shared/data-model/subscription-channel-rules';
import { shouldDispatchPaddleResume } from './paddle-subscription-policy';

@Injectable()
export class SubscriptionManager {
  // state populated by refreshAsync
  provider: SubscriptionProvider | null = null;
  canManageSubscription = false;
  canCancelSubscription = false;
  canResumeSubscription = false;
  // the raw subscription status the server reported, so the page can tell the
  // difference between states the boolean flags above flatten together. Null
  // until refreshAsync runs. (#4247)
  subscriptionStatus: SubscriptionStatus | null = null;
  currentPlan: PlanOption | null = null;
  otherPlan: PlanOption | null = null;

  // plan name ('Monthly' / 'Yearly') the server resolved in getSubscriptionInfo.
  // Used by the Paddle plan loader to build currentPlan without a second
  // price→period map on the client. Null until refreshAsync runs. (#4189)
  // Written only by refreshAsync; public so PaddleSubscriptionStrategy can read it.
  serverPlanName: string | null = null;
  // the Paddle monthly ↔ yearly switch target the server resolved from the
  // subscription's own env. Populating otherPlan from it is what arms the
  // shared canSwitchPlan gate for Paddle (#4189 Phase 3).
  // Written only by refreshAsync; public so PaddleSubscriptionStrategy can read it.
  paddleSwitchTarget: PaddleSwitchTarget | null = null;

  // lazily-built per-provider strategy; invalidated when `provider` changes
  // (see refreshAsync). Mirror of CameraService::strategy.
  private _strategy: SubscriptionStrategy | null = null;

  get strategy(): SubscriptionStrategy {
    if (!this._strategy) this._strategy = createSubscriptionStrategy(this);
    return this._strategy;
  }

  /** True when the membership is still live but its renewal payment is FAILING
   *  — Paddle's `past_due` dunning window, stored as Suspended. The page uses
   *  this to explain why a subscription that still shows a renewal date needs
   *  attention, and to keep Cancel reachable throughout (issue #4247). The
   *  provider asymmetry and the already-scheduled-cancel suppression both live
   *  in the shared predicate, which the server reads too. */
  get isPaymentFailing(): boolean {
    return isWebBilledPaymentFailing(
      this.provider,
      this.subscriptionStatus,
      this.auth.couch?.paddleData?.scheduledChange?.action === 'cancel',
    );
  }

  get canSwitchPlan(): boolean {
    // canCancelSubscription gates on "active subscriber" — downgrade and upgrade
    // both require an active line. otherPlan.saving used to be required here, but
    // saving is only populated on the yearly plan (the percent-off-vs-monthly
    // string), so requiring it blocked yearly→monthly downgrades. The HTML
    // template already branches on otherPlan.saving to pick upgrade-vs-downgrade
    // button styling.
    // pendingPlanChange hides the button while an Apple downgrade is queued
    // for end-of-cycle — tapping Switch again during that window would just
    // re-queue the same change.
    // isPaymentFailing keeps the button away while a Paddle renewal charge is
    // failing: canCancelSubscription now stays true through that window (so the
    // user can still get out — issue #4247), but a plan change on a subscription
    // that cannot collect payment is not something to offer.
    return this.canManageSubscription &&
      !!this.currentPlan && !!this.otherPlan &&
      this.canCancelSubscription &&
      !this.isPaymentFailing &&
      !this.pendingPlanChange;
  }

  /** Server-tracked queued renewal-pref change. Reads
   *  `appleData.autoRenewProductId` (set by `appleValidate` /
   *  `reconcileAppleSubscriptionAsync` / `appleServerNotification` webhook
   *  from Apple's signed renewalInfo JWS). When it differs from the active
   *  `appleData.productId`, Apple has scheduled a renewal-pref change for
   *  end-of-cycle (typically a downgrade triggered by Switch plan). Returns
   *  null otherwise. Apple's `DID_RENEW` event naturally clears the pending
   *  state by flipping `productId` to the previously-queued product — no
   *  client bookkeeping needed.
   *
   *  Returns null (rather than an object with an empty title) when otherPlan
   *  hasn't loaded yet or otherwise doesn't match the queued product — the
   *  pending UI is hidden during that window and surfaces cleanly on the
   *  next refresh once plans are populated. */
  get pendingPlanChange(): { toTitle: string } | null {
    if (this.provider !== 'apple') return null;
    const data = this.auth.couch?.appleData;
    if (!data?.autoRenewProductId || !data.productId) return null;
    if (data.autoRenewProductId === data.productId) return null;
    if (this.otherPlan?.id !== data.autoRenewProductId) return null;
    return { toTitle: this.otherPlan.title };
  }

  get needsCancellationReason(): boolean {
    return this.provider === 'payPro';
  }

  /** True while the subscription is inside its free trial. Used to refuse a
   *  plan switch that the provider would reject anyway (#4189 Phase 3).
   *
   *  Delegates to the CANONICAL shared rule rather than restating it. That
   *  helper exists precisely because two hand-written copies had already
   *  drifted (#4184), so a third copy here would be the exact mistake it was
   *  introduced to prevent.
   *
   *  Deliberately asks the PADDLE-only rule (#4248 Phase 7), and is named for
   *  that scope so a future caller cannot mistake it for a general answer. The
   *  server's switch-plan body and the admin capability matrix ask the SAME
   *  helper, so all three copies of "may this subscription switch plan?" agree.
   *
   *  Why not the all-provider `isSubscriberInFreeTrial`: it also fires on
   *  `appleData.inTrialPeriod`, `googlePlayData.inTrialPeriod` and a future
   *  PayPro `trialEnds_utc`, and those flags are only rewritten when that
   *  provider's own reconcile runs. A paying Paddle customer carrying a stale
   *  block from an earlier provider would be refused a switch they are entitled
   *  to, with a message about a trial they are not in. Only the Paddle
   *  subscription's own trial state can be relevant to switching it. */
  get isPaddleSubscriptionInFreeTrial(): boolean {
    const couch = this.auth.couch;
    if (!couch) return false;
    return isPaddleSubscriberInFreeTrial(couch.paddleData);
  }

  // cloudFunctions / auth / payProPriceFetcher / iapService / logging are public-readonly so the
  // provider-specific strategies (subscription-strategy.ts) can read them
  // via their manager reference — same shape as CameraService exposing
  // openCameraAsync/activeFacingMode publicly to WebCameraStrategy.
  constructor(
    public readonly cloudFunctions: CloudFunctionsService,
    public readonly auth: AuthService,
    public readonly payProPriceFetcher: PayProPriceFetcher,
    public readonly iapService: IAPService,
    public readonly logging: LoggingService,
  ) {}

  /** Refresh subscription info from server + load plans if manageable */
  async refreshAsync(): Promise<void> {
    const info = await this.cloudFunctions.getSubscriptionInfoAsync({
      platform: Capacitor.getPlatform() as 'android' | 'ios' | 'web',
    });
    if (!info) return;

    const provider = info.provider ?? null;
    // if the provider changed, rebuild on next get strategy(). Only then: the
    // Google Play strategy holds the store product behind otherPlan, and the
    // page can still offer the switch from the previous refresh while this one
    // is loading plans — a strategy thrown away here would answer that tap with
    // 'No alternate plan.'.
    if (provider !== this.provider) this._strategy = null;
    this.provider = provider;
    this.canManageSubscription = info.canManageSubscription ?? false;
    this.canCancelSubscription = info.canCancelSubscription ?? false;
    this.canResumeSubscription = info.canResumeSubscription ?? false;
    this.subscriptionStatus = info.subscriptionStatus ?? null;
    this.serverPlanName = info.planName ?? null;
    this.paddleSwitchTarget = info.paddleSwitchTarget ?? null;

    // re-login to pick up any status changes from server re-validation
    await this.auth.attemptCouchLoginAsync(CouchLoginReason.SubscriptionRevalidated);

    // defensive data-consistency guard: if server tells us the Apple
    // subscription is still active but the recorded Apple expiresDate is
    // well in the past, something is wrong upstream (server reconcile
    // failed to transition the user to Terminated, or Apple's API state
    // diverged from our cached record). Surface to Sentry so we can
    // investigate before users see a phantom-active subscription. 24h
    // buffer accounts for grace-period offsets and clock skew.
    this.reportApplePastExpiryIfActive();

    if (this.canManageSubscription) {
      await this.loadPlansAsync();
    }
  }

  /** See refreshAsync — defensive instrumentation for the active-status
   *  vs. past-expiresDate divergence class of bug. Logs once per refresh
   *  if the inconsistency is present. */
  private reportApplePastExpiryIfActive(): void {
    if (this.provider !== 'apple') return;
    const couch = this.auth.couch;
    if (!couch || couch.subscriptionStatus !== SubscriptionStatus.Active) return;
    const expiresIso = couch.appleData?.expiresDate_utc;
    if (!expiresIso) return;
    const expiresMs = Date.parse(expiresIso);
    if (Number.isNaN(expiresMs)) return;
    // 24h buffer = grace period max + clock skew tolerance
    const cutoffMs = Date.now() - 24 * 60 * 60 * 1000;
    if (expiresMs >= cutoffMs) return;
    this.logging.trackSilentProblem('Apple subscription reported active with expiresDate >24h in the past', {
      expiresDate_utc: expiresIso,
      lastValidated_utc: couch.appleData?.lastValidated_utc,
      productId: couch.appleData?.productId,
      autoRenewProductId: couch.appleData?.autoRenewProductId,
    });
  }

  /**
   * Cancel subscription. For PayPro, caller must collect reason first.
   * See CancelSubscriptionResult for the Paddle-only `alreadyScheduled` (#4629).
   */
  async cancelAsync(params?: CancelSubscriptionParams): Promise<CancelSubscriptionResult> {
    // if the provider is unknown there is no strategy to ask — the factory
    // throws on it — and the page expects an answer, not an exception
    if (!this.provider) return { error: 'No active subscription.' };
    return this.strategy.cancel(params);
  }

  /** Resume subscription */
  async resumeAsync(): Promise<{ error?: string }> {
    // A PAUSED Paddle sub must resume even when `provider` is unknown. `provider`
    // is populated only from getSubscriptionInfo, so after a transient failure
    // there is no provider to choose a strategy from — and the profile page
    // deliberately still offers Resume in that state (the paused block is known
    // from local session data). So this check stays here, before delegation, and
    // builds the Paddle strategy directly; the one Paddle resume call is still
    // written once, in PaddleSubscriptionStrategy.resume. (#4189)
    const isPaddleResume = shouldDispatchPaddleResume(
      this.provider,
      isPausedPaddleSubscriber(this.auth.couch),
    );
    if (isPaddleResume) {
      return new PaddleSubscriptionStrategy(this).resume();
    }
    if (!this.provider) return { error: 'No subscription to resume.' };
    return this.strategy.resume();
  }

  // There is deliberately no pauseAsync (#4189): the app never schedules a
  // pause. It still recognises and resumes one, because Paddle Retain can pause
  // a subscription from the hosted portal. Reasoning:
  // design-docs/paddle-integration.md, "Pause: handled, never offered".

  /** Switch to alternate plan via provider-specific strategy. */
  async switchPlanAsync(): Promise<SwitchPlanResult> {
    return this.strategy.switchPlan();
  }

  private async loadPlansAsync(): Promise<void> {
    // if the provider is unknown there are no plans to load
    if (!this.provider) return;
    await this.strategy.loadPlans();
  }
}
